// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xProductPartNumbersStyles from "@adl/App/SharedStyle/ProductPartNumbers.styles";
import { color, ellipsis } from "@adl/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { BrandDto, BrandModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    /** XNG-Changes */
    showBrand?: boolean;
    brand?: BrandModel | BrandDto | null;
    showLastOrdered?: boolean;
    lastOrdered?: string;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    /** XNG-Changes */
    brandGridItem?: GridItemProps;
    brandLabelText?: TypographyPresentationProps;
    brandValueText?: TypographyPresentationProps;
    brandLink?: LinkPresentationProps;
    lastOrderedLabelText?: TypographyPresentationProps;
    lastOrderedValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 4 },
    erpNumberLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    erpNumberValueText: {
        ...xProductPartNumbersStyles.value,
    },
    customerNameLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    customerNameValueText: {
        ...xProductPartNumbersStyles.value,
    },
    manufacturerItemLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    manufacturerItemValueText: {
        ...xProductPartNumbersStyles.value,
    },
    brandLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    brandValueText: {
        ...xProductPartNumbersStyles.value,
    },
    brandLink: {
        color: "text.main",

        typographyProps: {
            mergeCss: true,
            ...xProductPartNumbersStyles.value,
        },

        css: css`
            &&& {
                display: inline-flex;
            }
            &:hover {
                color: ${color("common.ui1")};
            }
            &&&:focus {
                outline: none;
            }
            ${ellipsis(1)}
        `,
    },
    erpNumberGridItem: {
        css: css`
            padding-top: 0px;
            padding-bottom: 0px;
        `,
    },
    customerNameGridItem: {
        css: css`
            padding-top: 0px;
            padding-bottom: 0px;
        `,
    },
    manufacturerItemGridItem: {
        css: css`
            padding-bottom: 0px;
        `,
    },
    lastOrderedLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    lastOrderedValueText: {
        ...xProductPartNumbersStyles.value,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    showBrand = true,
    showLastOrdered = false,
    lastOrdered,
    brand,
    extendedStyles,
    showProductNumber = true,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;
    const displayBrand = showBrand && brand ? brand?.name : false;
    const displayLastOrdered = showLastOrdered && lastOrdered;

    // XNG-Changes: re-order elements and add brand item
    return (
        <GridContainer {...styles.container}>
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </GridItem>
            )}
            {showProductNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {productNumber}
                    </Typography>
                </GridItem>
            )}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </GridItem>
            )}

            {displayBrand && brand && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.brandGridItem}>
                    {showLabel && <Typography {...styles.brandLabelText}>{translate("Brand")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.brandLabelText}>{translate("Brand")} </Typography>
                        </VisuallyHidden>
                    )}
                    {brand.detailPagePath ? (
                        <Link
                            {...styles.brandLink}
                            href={brand.detailPagePath}
                            alt={`See product details for ${brand.detailPagePath}`}
                        >
                            {brand.name}
                        </Link>
                    ) : (
                        <Typography {...styles.brandValueText}>{brand.name}</Typography>
                    )}
                </GridItem>
            )}
            {displayLastOrdered && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.brandGridItem}>
                    {showLabel && <Typography {...styles.lastOrderedLabelText}>{translate("Last Ordered")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.lastOrderedLabelText}>{translate("Last Ordered")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.lastOrderedValueText} data-test-selector="lastOrdered">
                        {lastOrdered}
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default ProductPartNumbers;
